import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { 
  Heart, Settings, LogOut, Bike, ShoppingBag, Trash2, Utensils, 
  Store, Handshake, ChevronRight, Shield, Link2, Loader2, KeyRound,
  Mail, UserCircle
} from "lucide-react";
import { Link } from "wouter";
import { Restaurant } from "@/lib/types";
import { useFavorites } from "@/hooks/use-favorites";
import { apiRequest, queryClient as qc } from "@/lib/queryClient";

const ProfilePage = () => {
  const { user, logoutMutation } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("favorites");
  const { toggleFavorite } = useFavorites();
  const queryClient = useQueryClient();
  
  // Profile form state
  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [email, setEmail] = useState(user?.email || "");
  
  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Favoriten-Restaurants
  const { data: favoriteRestaurants = [], isLoading } = useQuery<Restaurant[]>({
    queryKey: ["/api/users/favorites"],
    enabled: !!user,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  // Linked OAuth accounts
  const { data: linkedAccounts = [] } = useQuery<{ provider: string; linkedAt: string }[]>({
    queryKey: ["/api/auth/linked-accounts"],
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Profile update mutation
  const profileMutation = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; email: string }) => {
      const res = await apiRequest("PUT", "/api/auth/profile", data);
      return await res.json();
    },
    onSuccess: (updatedUser) => {
      qc.setQueryData(["/api/auth/me"], updatedUser);
      try {
        localStorage.setItem('speiseguru_user', JSON.stringify(updatedUser));
      } catch {}
      toast({ title: "Profil aktualisiert", description: "Ihre Änderungen wurden gespeichert." });
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    },
  });

  // Password change mutation
  const passwordMutation = useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const res = await apiRequest("PUT", "/api/auth/password", data);
      return await res.json();
    },
    onSuccess: () => {
      toast({ title: "Passwort geändert", description: "Ihr Passwort wurde erfolgreich aktualisiert." });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (error: Error) => {
      toast({ title: "Fehler", description: error.message, variant: "destructive" });
    },
  });

  const getRestaurantImage = (restaurant: Restaurant): string | null => {
    if (restaurant.images && restaurant.images.length > 0) {
      const img = restaurant.images.find((i: any) => !i.is_menu) || restaurant.images[0];
      const path = img.path || (img as any).url;
      if (path) {
        if (path.startsWith('http')) return path;
        if (path.startsWith('/')) return path;
        return `/images/${path}`;
      }
    }
    return null;
  };

  const handleRemoveFavorite = (restaurantId: string, restaurantName: string) => {
    toggleFavorite(restaurantId);
    queryClient.setQueryData<Restaurant[]>(["/api/users/favorites"], (old) => {
      return old ? old.filter(r => r.id !== restaurantId) : [];
    });
    toast({ title: `${restaurantName} aus Favoriten entfernt` });
  };

  const handleProfileSave = (e: React.FormEvent) => {
    e.preventDefault();
    profileMutation.mutate({ firstName, lastName, email });
  };

  const handlePasswordChange = () => {
    if (!newPassword) {
      toast({ title: "Fehler", description: "Bitte geben Sie ein neues Passwort ein", variant: "destructive" });
      return;
    }
    if (newPassword.length < 6) {
      toast({ title: "Fehler", description: "Passwort muss mindestens 6 Zeichen haben", variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Fehler", description: "Die Passwörter stimmen nicht überein", variant: "destructive" });
      return;
    }
    passwordMutation.mutate({ currentPassword, newPassword });
  };

  if (!user) {
    return (
      <div className="container max-w-7xl mx-auto py-16 px-4">
        <div className="text-center">
          <h1 className="text-3xl font-bold mb-6">Profilseite</h1>
          <p className="mb-6">Sie müssen angemeldet sein, um diese Seite zu sehen.</p>
          <Link href="/auth" className="bg-primary text-white px-4 py-2 rounded-md">Zum Login</Link>
        </div>
      </div>
    );
  }

  const displayName = user.firstName 
    ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}`
    : user.username || user.email?.split('@')[0];

  const avatarInitials = user.firstName 
    ? `${user.firstName[0]}${user.lastName ? user.lastName[0] : ''}`
    : (user.email?.slice(0, 2) || 'U').toUpperCase();

  return (
    <div className="bg-gray-50 min-h-screen pb-16">
      <div className="container max-w-7xl mx-auto py-8 md:py-16 px-4">
        <div className="flex flex-col md:flex-row gap-8">
          
          {/* Sidebar */}
          <div className="w-full md:w-1/4 space-y-4">
            {/* User Card */}
            <Card className="overflow-hidden">
              <div className="h-20 bg-gradient-to-br from-[#0E7F4F] to-[#18B66A]" />
              <CardContent className="pt-0 -mt-10 pb-5">
                <div className="flex flex-col items-center text-center">
                  {user.avatarUrl ? (
                    <img
                      src={user.avatarUrl}
                      alt={displayName}
                      className="w-20 h-20 rounded-full border-4 border-white shadow-md object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full border-4 border-white shadow-md bg-[#0E7F4F] flex items-center justify-center">
                      <span className="text-white text-xl font-bold">{avatarInitials}</span>
                    </div>
                  )}
                  <h2 className="mt-3 font-semibold text-gray-900">{displayName}</h2>
                  <p className="text-sm text-gray-500">{user.email}</p>
                  
                  {/* Linked Providers Badges */}
                  {linkedAccounts.length > 0 && (
                    <div className="flex gap-1.5 mt-2">
                      {linkedAccounts.map(a => (
                        <span key={a.provider} className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full capitalize">
                          <Link2 className="w-3 h-3" />
                          {a.provider}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <Card>
              <CardContent className="py-3">
                <div className="space-y-1">
                  <Button variant="ghost" className={`w-full justify-start ${activeTab === "favorites" ? "bg-[#0E7F4F]/10 text-[#0E7F4F]" : ""}`} onClick={() => setActiveTab("favorites")}>
                    <Heart className="mr-2 h-4 w-4" /> Meine Favoriten
                  </Button>
                  <Button variant="ghost" className={`w-full justify-start ${activeTab === "settings" ? "bg-[#0E7F4F]/10 text-[#0E7F4F]" : ""}`} onClick={() => setActiveTab("settings")}>
                    <Settings className="mr-2 h-4 w-4" /> Einstellungen
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Partner & Restaurant Links */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-gray-500 uppercase tracking-wider">Für Unternehmen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 pt-0">
                <Link href="/partner">
                  <button className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-[#0E7F4F]/5 transition-colors group text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                        <Handshake className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Partner werden</p>
                        <p className="text-xs text-gray-500">Affiliate-Programm beitreten</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#0E7F4F] transition-colors" />
                  </button>
                </Link>
                
                <Link href="/restaurant-anmelden">
                  <button className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg hover:bg-[#0E7F4F]/5 transition-colors group text-left">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#0E7F4F] to-[#18B66A] flex items-center justify-center">
                        <Store className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Restaurant anmelden</p>
                        <p className="text-xs text-gray-500">Als Restaurantbesitzer registrieren</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#0E7F4F] transition-colors" />
                  </button>
                </Link>
              </CardContent>
            </Card>

            {/* Logout */}
            <Button 
              variant="ghost" 
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => logoutMutation.mutate()}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Abmelden
              {logoutMutation.isPending && (
                <Loader2 className="ml-auto h-4 w-4 animate-spin" />
              )}
            </Button>
          </div>
          
          {/* Main Content */}
          <div className="w-full md:w-3/4">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="mb-8 bg-gray-100 p-1">
                <TabsTrigger value="favorites" className="flex items-center">
                  <Heart className="mr-2 h-4 w-4" /> Favoriten
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" /> Einstellungen
                </TabsTrigger>
              </TabsList>
              
              {/* Favoriten Tab */}
              <TabsContent value="favorites">
                <Card>
                  <CardHeader>
                    <CardTitle>Meine Favoriten</CardTitle>
                    <CardDescription>Restaurants, die du als Favoriten gespeichert hast.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {isLoading ? (
                      <div className="space-y-4">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex items-center space-x-4">
                            <Skeleton className="h-16 w-16 rounded-md" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-48" />
                              <Skeleton className="h-4 w-24" />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : Array.isArray(favoriteRestaurants) && favoriteRestaurants.length > 0 ? (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {favoriteRestaurants.map((restaurant) => {
                          const imageUrl = getRestaurantImage(restaurant);
                          const hasDelivery = restaurant.delivery || (restaurant as any).has_delivery;
                          const hasPickup = (restaurant as any).pickup || (restaurant as any).has_pickup;
                          return (
                            <Card key={restaurant.id} className="overflow-hidden group hover:shadow-md transition-shadow">
                              <div className="flex">
                                <Link href={`/restaurants/${(restaurant as any).slug || restaurant.id}`} className="flex flex-1 min-w-0">
                                  <div className="h-24 w-24 bg-gray-100 shrink-0 overflow-hidden">
                                    {imageUrl ? (
                                      <img src={imageUrl} alt={restaurant.name} className="h-full w-full object-cover"
                                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="h-full w-full flex items-center justify-center bg-gradient-to-br from-[#E8F5EE] to-[#D0EBE0]">
                                        <Utensils className="w-7 h-7 text-[#0E7F4F]" />
                                      </div>
                                    )}
                                  </div>
                                  <div className="p-3 flex-1 min-w-0 cursor-pointer">
                                    <h3 className="font-semibold text-gray-900 truncate">{restaurant.name}</h3>
                                    <p className="text-xs text-gray-500 mt-0.5 truncate">{restaurant.city}</p>
                                    <div className="flex items-center gap-2 mt-2">
                                      {hasDelivery && (
                                        <span className="inline-flex items-center gap-1 text-xs text-[#0E7F4F] bg-[#0E7F4F]/8 px-2 py-0.5 rounded-full font-medium">
                                          <Bike className="w-3 h-3" /> Lieferung
                                        </span>
                                      )}
                                      {(hasPickup || !hasDelivery) && (
                                        <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full font-medium">
                                          <ShoppingBag className="w-3 h-3" /> Abholung
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </Link>
                                <button
                                  onClick={() => handleRemoveFavorite(restaurant.id, restaurant.name)}
                                  className="self-center px-3 py-2 mr-2 text-gray-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50"
                                  title="Aus Favoriten entfernen"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Heart className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Keine Favoriten</h3>
                        <p className="text-gray-500 mb-4">Du hast noch keine Restaurants als Favoriten gespeichert.</p>
                        <Link href="/restaurants" className="text-primary hover:underline">Restaurants entdecken</Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Einstellungen Tab */}
              <TabsContent value="settings">
                <div className="space-y-6">
                  {/* Profile Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <UserCircle className="h-5 w-5 text-[#0E7F4F]" />
                        <CardTitle>Persönliche Daten</CardTitle>
                      </div>
                      <CardDescription>Verwalte deine persönlichen Informationen.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <form onSubmit={handleProfileSave}>
                        <div className="space-y-5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label htmlFor="firstName" className="text-sm font-medium text-gray-700">Vorname</label>
                              <Input id="firstName" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full" placeholder="Dein Vorname" />
                            </div>
                            <div className="space-y-2">
                              <label htmlFor="lastName" className="text-sm font-medium text-gray-700">Nachname</label>
                              <Input id="lastName" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full" placeholder="Dein Nachname" />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-medium text-gray-700">E-Mail-Adresse</label>
                            <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="max-w-md" />
                          </div>
                          
                          <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Benutzername</label>
                            <Input value={user.username || ""} className="max-w-md bg-gray-50" disabled />
                            <p className="text-xs text-gray-500">Der Benutzername kann nicht geändert werden.</p>
                          </div>
                          
                          <Button type="submit" className="bg-[#0E7F4F] hover:bg-[#0D6F43]" disabled={profileMutation.isPending}>
                            {profileMutation.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Speichern...</>) : 'Profil speichern'}
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>

                  {/* Password Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-5 w-5 text-[#0E7F4F]" />
                        <CardTitle>Passwort ändern</CardTitle>
                      </div>
                      <CardDescription>Aktualisiere dein Passwort regelmäßig für mehr Sicherheit.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <label htmlFor="currentPassword" className="text-sm font-medium text-gray-700">Aktuelles Passwort</label>
                          <Input id="currentPassword" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="max-w-md" placeholder="Dein aktuelles Passwort" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label htmlFor="newPassword" className="text-sm font-medium text-gray-700">Neues Passwort</label>
                            <Input id="newPassword" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min. 6 Zeichen" />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="confirmNewPassword" className="text-sm font-medium text-gray-700">Passwort bestätigen</label>
                            <Input id="confirmNewPassword" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Passwort wiederholen" />
                          </div>
                        </div>
                        <Button onClick={handlePasswordChange} variant="outline" disabled={passwordMutation.isPending}>
                          {passwordMutation.isPending ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Ändern...</>) : 'Passwort ändern'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Connected Accounts Card */}
                  <Card>
                    <CardHeader>
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-[#0E7F4F]" />
                        <CardTitle>Verknüpfte Konten</CardTitle>
                      </div>
                      <CardDescription>Social-Login-Konten, die mit deinem Profil verbunden sind.</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {/* Google */}
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Google</p>
                              <p className="text-xs text-gray-500">
                                {linkedAccounts.find(a => a.provider === 'google') ? 'Verknüpft' : 'Nicht verknüpft'}
                              </p>
                            </div>
                          </div>
                          {linkedAccounts.find(a => a.provider === 'google') ? (
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">Verbunden</span>
                          ) : (
                            <a href="/api/auth/google" className="text-xs font-medium text-[#0E7F4F] hover:text-[#0D6F43] bg-[#0E7F4F]/10 px-3 py-1.5 rounded-full transition-colors">
                              Verknüpfen
                            </a>
                          )}
                        </div>

                        {/* Facebook */}
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#1877F2">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-gray-900">Facebook</p>
                              <p className="text-xs text-gray-500">
                                {linkedAccounts.find(a => a.provider === 'facebook') ? 'Verknüpft' : 'Nicht verknüpft'}
                              </p>
                            </div>
                          </div>
                          {linkedAccounts.find(a => a.provider === 'facebook') ? (
                            <span className="text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">Verbunden</span>
                          ) : (
                            <a href="/api/auth/facebook" className="text-xs font-medium text-[#0E7F4F] hover:text-[#0D6F43] bg-[#0E7F4F]/10 px-3 py-1.5 rounded-full transition-colors">
                              Verknüpfen
                            </a>
                          )}
                        </div>

                        {/* Email/Password */}
                        <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                              <Mail className="w-3.5 h-3.5 text-gray-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">E-Mail & Passwort</p>
                              <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                          </div>
                          <span className="text-xs font-medium text-green-600 bg-green-50 px-2.5 py-1 rounded-full">Aktiv</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
